import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { devicesService, Device, CreateDeviceData } from '../../services/devices'

export const useDevices = (companyId?: string) => {
  return useQuery({
    queryKey: ['devices', companyId ?? 'all'],
    queryFn: () => devicesService.getDevices(companyId),
    enabled: companyId !== undefined ? companyId !== '' : true
  })
}

export const useDevice = (deviceId: string) => {
  return useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => devicesService.getDevice(deviceId),
    enabled: !!deviceId
  })
}

export const useCreateDevice = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceData, companyId, sendAgentNow }: { deviceData: CreateDeviceData; companyId: string; sendAgentNow?: boolean }) =>
      devicesService.createDevice(deviceData, companyId, sendAgentNow),
    onSuccess: (_, { companyId }) => {
      // Invalidate and refetch devices list
      queryClient.invalidateQueries({ queryKey: ['devices', companyId] })
      queryClient.invalidateQueries({ queryKey: ['devices', 'all'] })
    }
  })
}

export const useUpdateDevice = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceId, updates }: { deviceId: string; updates: Partial<Device> }) =>
      devicesService.updateDevice(deviceId, updates),
    onSuccess: (updatedDevice) => {
      // Update the specific device in cache
      queryClient.setQueryData(['device', updatedDevice.id], updatedDevice)
      // Invalidate devices list to refetch
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['devices', 'all'] })
    }
  })
}

export const useDeleteDevice = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (deviceId: string) => devicesService.deleteDevice(deviceId),
    onSuccess: (_, deviceId) => {
      // Remove device from cache
      queryClient.removeQueries({ queryKey: ['device', deviceId] })
      // Invalidate devices list to refetch
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['devices', 'all'] })
    }
  })
}
